import type {
  AvailableRefereeRewardsCountDto,
  MarkRefereesViewedDto,
  RefereeCountDto,
  UserRefereeListDto,
  UserRefereeParams,
  UserRefereeSummaryDto,
  UserReferralDto,
} from './referral.schema';
import { validateWithZod } from '@/utils/zod.utils';
import { ENDPOINTS } from '../endpoints';
import { APIError, httpClient } from '../httpClient';
import { PaginationSchema } from '../types/Pagination.types';
import {
  AvailableRefereeRewardsCountDtoSchema,
  MarkRefereesViewedDtoSchema,
  RefereeCountDtoSchema,
  UserRefereeDtoSchema,
  UserRefereeSummaryDtoSchema,
  UserReferralDtoSchema,
} from './referral.schema';

export const referralApi = {

  /**
   * Resolves to `null` while the user has no referral code yet.
   * The code is created automatically once the user reaches Bronze status.
   */
  async getMeReferral(): Promise<UserReferralDto | null> {
    try {
      const response = await httpClient.get<UserReferralDto>(ENDPOINTS.referral.me);

      return validateWithZod(UserReferralDtoSchema, response);
    } catch (error) {
      if (error instanceof APIError && error.status === 404) {
        return null;
      }
      throw error;
    }
  },

  async getMeReferees(params?: UserRefereeParams) {
    const response = await httpClient.get<UserRefereeListDto>(
      ENDPOINTS.referral.meReferees,
      { params },
    );

    return validateWithZod(PaginationSchema(UserRefereeDtoSchema), response);
  },

  async getMeRefereesSummary() {
    const response = await httpClient.get<UserRefereeSummaryDto>(ENDPOINTS.referral.meRefereesSummary);

    return validateWithZod(UserRefereeSummaryDtoSchema, response);
  },
  /** @deprecated Use `getRefereesCount` instead. */
  async getMeRefereesAvailableRewardsCount() {
    const response = await httpClient.get<AvailableRefereeRewardsCountDto>(
      ENDPOINTS.referral.meRefereesAvailableRewardsCount,
    );

    return validateWithZod(AvailableRefereeRewardsCountDtoSchema, response);
  },
  async getMeRefereesCount() {
    const response = await httpClient.get<RefereeCountDto>(
      ENDPOINTS.referral.meRefereesCount,
    );

    return validateWithZod(RefereeCountDtoSchema, response);
  },

  async markMeRefereesViewed(data: MarkRefereesViewedDto) {
    const payload = MarkRefereesViewedDtoSchema.parse(data);

    await httpClient.patch<void>(ENDPOINTS.referral.meRefereesViewed, payload);
  },

  /**
   * Claims all available rewards for one invited user. Wallet balance arrives via `wallet.balance.update`.
   * Fails with 404 when nothing is available and 409 when the rewards were already claimed.
   */
  async claimRefereeRewards(refereeUserId: string) {
    await httpClient.post<void>(ENDPOINTS.referral.claimRefereeRewards(refereeUserId));
  },

};
